import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Paperclip, Reply, ReplyAll, Search, Download, Inbox, Plus, Trash2, Settings2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { api } from "../lib/api";
import { saveBlob } from "../lib/download";
import { QuickReplies } from "./OutlookQuickReplies";

const err = (e, d) => toast.error(e?.response?.data?.detail || d);
const when = (d) => (d ? new Date(d).toLocaleString("ar-SA-u-nu-latn", { dateStyle: "short", timeStyle: "short" }) : "");
const escHTML = (t) => t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br/>");

export const MailboxManager = ({ boxes, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setItems(boxes.filter((b) => !b.primary).map((b) => ({ address: b.address, name: b.name }))); }, [open, boxes]);
  const lockAct = async (b, remove) => {
    try {
      const old = b.locked ? window.prompt("الرقم السري الحالي") : "";
      if (b.locked && old === null) return;
      if (remove) { await api.removeMailboxLock(b.address, old); toast.success("تم إلغاء القفل"); }
      else {
        const pin = window.prompt(`رقم سري جديد لـ ${b.address} (6 أرقام)`);
        if (!pin) return;
        await api.setMailboxLock(b.address, pin, old); toast.success("تم قفل البريد بالرقم السري");
      }
      onSaved((await api.outlookMailboxes()).items);
    } catch (e) { err(e, "تعذّر التنفيذ"); }
  };
  const setSystem = async (b) => { try { onSaved((await api.setSystemMailbox(b.address)).items); toast.success(`تنبيهات النظام تُرسل من ${b.address}`); } catch (e) { err(e, "تعذّر الحفظ"); } };
  const save = async () => {
    setBusy(true);
    try { const r = await api.saveOutlookMailboxes(items.filter((x) => x.address.trim())); onSaved(r.items); toast.success("تم حفظ صناديق البريد"); setOpen(false); }
    catch (e) { err(e, "تعذّر الحفظ"); } finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline" data-testid="outlook-mailboxes-manage" onClick={() => setOpen(true)} className="gap-1.5"><Settings2 className="h-4 w-4" /> صناديق البريد</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md font-body" dir="rtl" data-testid="outlook-mailboxes-dialog">
          <DialogHeader><DialogTitle>صناديق البريد المربوطة</DialogTitle>
            <DialogDescription>أضف أي بريد على نطاق شركتك ليُرسل منه ويُستقبل عليه بنفس تطبيق Microsoft.</DialogDescription></DialogHeader>
          <div className="space-y-1.5 rounded-xl border border-slate-200 p-2.5" data-testid="outlook-mailbox-security">
            <p className="text-xs font-bold text-slate-600">القفل وتنبيهات النظام</p>
            {boxes.map((b) => (
              <div key={b.address} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                <span dir="ltr" className="truncate">{b.locked ? "🔒 " : ""}{b.address}</span>
                <div className="flex items-center gap-1.5">
                  <label className="flex items-center gap-1 text-[11px] text-slate-500"><input type="radio" name="sysmb" checked={!!b.system} onChange={() => setSystem(b)} data-testid={`outlook-system-${b.address}`} /> تنبيهات النظام</label>
                  <Button size="sm" variant="outline" className="h-7 text-[11px]" data-testid={`outlook-lock-${b.address}`} onClick={() => lockAct(b, false)}>{b.locked ? "تغيير الرمز" : "قفل برمز"}</Button>
                  {b.locked && <Button size="sm" variant="ghost" className="h-7 text-[11px] text-rose-600" data-testid={`outlook-unlock-remove-${b.address}`} onClick={() => lockAct(b, true)}>إلغاء القفل</Button>}
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            {boxes.filter((b) => b.primary).map((b) => <div key={b.address} className="rounded-lg bg-slate-50 px-3 py-2 text-sm" dir="ltr">{b.address} <span className="text-[11px] text-slate-400">(الرئيسي)</span></div>)}
            {items.map((m, i) => (
              <div key={i} className="flex gap-2">
                <Input data-testid={`outlook-mailbox-address-${i}`} dir="ltr" placeholder="it@company.sa" value={m.address} onChange={(e) => setItems((p) => p.map((x, j) => (j === i ? { ...x, address: e.target.value } : x)))} />
                <Input data-testid={`outlook-mailbox-name-${i}`} placeholder="الاسم الظاهر" value={m.name} onChange={(e) => setItems((p) => p.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                <button onClick={() => setItems((p) => p.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <Button variant="outline" size="sm" data-testid="outlook-mailbox-add" onClick={() => setItems((p) => [...p, { address: "", name: "" }])} className="gap-1"><Plus className="h-4 w-4" /> إضافة بريد</Button>
          </div>
          <DialogFooter><Button data-testid="outlook-mailboxes-save" disabled={busy} onClick={save}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "حفظ"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const MessageDialog = ({ msgId, mailbox, onClose }) => {
  const [m, setM] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!msgId) return;
    setM(null); setReply("");
    api.outlookMessage(msgId, mailbox).then(setM).catch((e) => { err(e, "تعذّر فتح الرسالة"); onClose(); });
  }, [msgId, mailbox]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async (all) => {
    if (!reply.trim()) return;
    setBusy(true);
    try { await api.outlookReply(msgId, { mailbox, reply_all: all, html: `<div dir="rtl" style="font-family:Tahoma,Arial;font-size:14px">${escHTML(reply)}</div>` }); toast.success("تم إرسال الرد ✅"); setReply(""); }
    catch (e) { err(e, "تعذّر الإرسال"); } finally { setBusy(false); }
  };
  const dl = async (a) => { try { await saveBlob(await api.outlookAttachment(msgId, a.id, mailbox), a.name); } catch (e) { err(e, "تعذّر التنزيل"); } };
  return (
    <Dialog open={!!msgId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl font-body max-h-[92vh] overflow-y-auto" dir="rtl" data-testid="outlook-message-dialog">
        {!m ? <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></div> : (
          <>
            <DialogHeader><DialogTitle className="text-right">{m.subject || "(بدون عنوان)"}</DialogTitle>
              <DialogDescription className="text-right"><span dir="ltr">{m.from?.name} &lt;{m.from?.address}&gt;</span> · {when(m.date)}{m.cc?.length ? ` · نسخة: ${m.cc.join(", ")}` : ""}</DialogDescription></DialogHeader>
            {m.attachments.length > 0 && (
              <div className="flex flex-wrap gap-1.5">{m.attachments.map((a) => (
                <button key={a.id} data-testid={`outlook-att-${a.id}`} onClick={() => dl(a)} className="flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs hover:bg-slate-100"><Download className="h-3 w-3" /> {a.name}</button>
              ))}</div>
            )}
            <iframe title="mail" sandbox="allow-popups allow-popups-to-escape-sandbox" srcDoc={`<base target="_blank"/>${m.html}`} className="h-[45vh] w-full rounded-xl border border-slate-200 bg-white" data-testid="outlook-message-body" />
            <QuickReplies senderName={m.from?.name} onPick={(txt) => setReply((p) => (p.trim() ? `${p}\n${txt}` : txt))} />
            <Textarea data-testid="outlook-reply-text" rows={4} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="اكتب ردك…" />
            <div className="flex gap-2">
              <Button data-testid="outlook-reply-send" disabled={busy || !reply.trim()} onClick={() => send(false)} className="gap-1.5">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Reply className="h-4 w-4" />} رد</Button>
              <Button variant="outline" data-testid="outlook-reply-all" disabled={busy || !reply.trim()} onClick={() => send(true)} className="gap-1.5"><ReplyAll className="h-4 w-4" /> رد على الكل</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const OutlookInbox = ({ mailbox }) => {
  const [folder, setFolder] = useState("inbox");
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [skip, setSkip] = useState(0);
  const [openId, setOpenId] = useState(null);
  const load = useCallback((s = 0, search = q) => {
    setError(""); if (s === 0) setData(null);
    api.outlookInbox({ mailbox, folder, skip: s, q: search })
      .then((r) => { setSkip(s); setData((p) => (s === 0 || !p ? r : { ...r, items: [...p.items, ...r.items] })); })
      .catch((e) => { setError(e?.response?.data?.detail || "تعذّر تحميل البريد"); setData({ items: [] }); });
  }, [mailbox, folder]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (mailbox) load(0); }, [load, mailbox]);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid="outlook-inbox">
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 p-3">
        {[["inbox", "الوارد"], ["sentitems", "المرسل"], ["junkemail", "غير المرغوب"]].map(([k, l]) => (
          <button key={k} data-testid={`outlook-folder-${k}`} onClick={() => setFolder(k)} className={`rounded-full px-3 py-1 text-xs font-semibold ${folder === k ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
        ))}
        <form className="flex flex-1 min-w-48 gap-1" onSubmit={(e) => { e.preventDefault(); load(0, q); }}>
          <Input data-testid="outlook-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث في البريد" className="h-8 text-sm" />
          <Button type="submit" size="sm" variant="outline" className="h-8"><Search className="h-4 w-4" /></Button>
        </form>
        <Button size="sm" variant="ghost" data-testid="outlook-refresh" onClick={() => load(0)} className="h-8"><RefreshCw className="h-4 w-4" /></Button>
      </div>
      {error && <p data-testid="outlook-inbox-error" className="m-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">{error}</p>}
      {!data ? <div className="py-12 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></div> : (
        <>
          {data.items.length === 0 && !error && <p className="py-12 text-center text-sm text-slate-400"><Inbox className="mx-auto mb-2 h-8 w-8" />لا توجد رسائل</p>}
          {data.items.map((m) => (
            <button key={m.id} data-testid="outlook-inbox-item" onClick={() => { setOpenId(m.id); m.read = true; }}
              className={`block w-full border-b border-slate-50 px-4 py-2.5 text-right transition-colors hover:bg-slate-50 ${m.read ? "" : "bg-blue-50/50"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className={`truncate text-sm ${m.read ? "text-slate-700" : "font-bold text-slate-900"}`}>{folder === "sentitems" ? m.to.join(", ") : (m.from?.name || m.from?.address)}</span>
                <span className="shrink-0 text-[11px] text-slate-400">{m.has_att && <Paperclip className="inline h-3 w-3 ml-1" />}{when(m.date)}</span>
              </div>
              <p className={`truncate text-sm ${m.read ? "text-slate-600" : "font-semibold text-slate-800"}`}>{m.subject}</p>
              <p className="truncate text-xs text-slate-400">{m.preview}</p>
            </button>
          ))}
          {data.more && <div className="p-3 text-center"><Button size="sm" variant="outline" data-testid="outlook-more" onClick={() => load(skip + 25)}>المزيد</Button></div>}
        </>
      )}
      <MessageDialog msgId={openId} mailbox={mailbox} onClose={() => setOpenId(null)} />
    </div>
  );
};
